<template>
    <div id="UserControlPreferences">
      <p>Control how you hear from us:</p>
      <div class="row">
        <div class="mb-5 col-12">
          <div class="row">
            <div class="col-8 d-flex align-items-center">
              Email
            </div>
            <div class="col">
              <label class="switch">
                <input type="checkbox"
                       :checked="user.user_settings.email_notifications"
                       @change="toggleEmail">
                <span class="slider round"></span>
              </label>
            </div>
          </div>
        </div>
        <div v-if="pushSupported" class="mb-5 col-12">
          <div class="row">
            <div class="col-8 d-flex align-items-center">
              Push Notifications
            </div>
            <div class="col">
              <label class="switch">
                <input type="checkbox"
                       :checked="user.user_settings.push_notifications"
                       @change="togglePush">
                <span class="slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
      <Popup v-if="showPushBlockedModal"
             :content="pushBlockedModalContent"
             v-on:close="showPushBlockedModal = false" />
    </div>
</template>

<script>

import axios from "axios";
import * as Sentry from "@sentry/vue";
import config from "@/config/constants";
import Popup from "@/components/Popup.vue"
import {mapState} from "vuex";

export default {
    name: "UserControlPreferences",
    components: {
        Popup
    },
    data() {
        return {
          showPushBlockedModal: false,
          pushBlockedModalContent: {
            header: "Push notifications are blocked",
            message: "You've previously blocked push notifications for this site. To turn them back on, open your browser's site settings, allow notifications for this domain, then return here and try again.",
            buttonText: "Got it",
          },
        };
    },
    mounted() {
      this.reconcilePushPermission();
    },
    computed: {
      ...mapState(['user']),
      componentText() {
        return this.getText.loggedIn.settings;
      },
      pushSupported() {
        return typeof window !== "undefined" && "Notification" in window;
      },
    },
    methods: {
      async reconcilePushPermission() {
        if (!this.pushSupported) return;
        if (
          Notification.permission === "denied" &&
          this.user.user_settings.push_notifications
        ) {
          this.user.user_settings.push_notifications = false;
          await this.persistSettings();
        }
      },

      async toggleEmail(event) {
        const newValue = event.target.checked;
        this.user.user_settings.email_notifications = newValue;
        const success = await this.persistSettings();
        if (!success) {
          this.user.user_settings.email_notifications = !newValue;
        }
      },

      async togglePush(event) {
        if (!this.pushSupported) return;
        const wantsToEnable = event.target.checked;

        if (!wantsToEnable) {
          this.user.user_settings.push_notifications = false;
          const success = await this.persistSettings();
          if (!success) this.user.user_settings.push_notifications = true;
          return;
        }

        if (Notification.permission === "granted") {
          this.user.user_settings.push_notifications = true;
          const success = await this.persistSettings();
          if (!success) this.user.user_settings.push_notifications = false;
          return;
        }

        if (Notification.permission === "denied") {
          event.target.checked = false;
          this.showPushBlockedModal = true;
          return;
        }

        const result = await Notification.requestPermission();
        if (result === "granted") {
          this.user.user_settings.push_notifications = true;
          const success = await this.persistSettings();
          if (!success) this.user.user_settings.push_notifications = false;
          return;
        }
        event.target.checked = false;
      },

      async persistSettings() {
        const payload = {
          user_id: this.$store.getters.user_id,
          user: this.user.user,
          user_profile: this.user.user_profile,
          user_settings: this.user.user_settings,
        };
        try {
          const response = await axios.post(
            config.api_env + "/application/api/u/user/update-user-profile",
            payload,
            config.options,
          );
          return response.data.success === true;
        } catch (error) {
          console.error("Failed to save notification preferences", error);
          Sentry.captureException(error, {
            tags: { component: "UserControlPreferences" },
            extra: { action: "persistSettings" },
          });
          return false;
        }
      },
    },
};
</script>

<style lang="less">
#UserControlPreferences {
  padding-bottom: 20px;
  .switch {
    position: relative;
    display: inline-block;
    width: 60px;
    height: 34px;
  }

  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    -webkit-transition: .4s;
    transition: .4s;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: #ccc;
    -webkit-transition: .4s;
    transition: .4s;
  }

  input:checked + .slider:before {
    background-color: #E83B75;
  ;
  }

  input:focus + .slider:before {
    box-shadow: 0 0 1px #E83B75;
  }

  input:checked + .slider:before {
    -webkit-transform: translateX(26px);
    -ms-transform: translateX(26px);
    transform: translateX(26px);
  }

  .slider.round {
    border: 1px solid #ccc;
    border-radius: 34px;
  }

  .slider.round:before {
    border-radius: 50%;
  }
}
</style>
